import React, { useEffect, useState } from "react";
import { LassoSimpleButtonFull } from "../../components/ui/utils/LassoButtons";
import TopMostPortal from "../../components/ui/utils/Portals";
import RestAPI from "../../utils/restAPI";
import { logError } from "../../shared/common/logger";
import { toastNotify } from "../../shared/client/FrontendUtils";
import ImageUploadField from "./PDFExportConfigModal/ImageUploadField";
import ArrayInputField from "./PDFExportConfigModal/ArrayInputField";
import HeaderInfoField, { EstimateFieldsForExportHeader, HeaderInfoItem } from "./PDFExportConfigModal/HeaderInfoField";
import LetterHeadManagement from "./PDFExportConfigModal/LetterHeadManagement";
import OfficeFooterManagement from "./PDFExportConfigModal/OfficeFooterManagement";
import VerboseSowPdfModeField, { VerboseSowPdfMode } from "./PDFExportConfigModal/VerboseSowPdfModeField";

export interface LetterHeadDetails {
	path: string;
	scale: number;
	margin: {
		top: number;
		bottom: number;
		left: number;
		right: number;
	};
	previewScale?: number;
	previewAlignment?: "left" | "center" | "right";
	officeNames: string[]; // list of office names that this letterhead applies to (applies to all offices if empty)
}

export interface OfficeFooter {
	officeNames: string[];
	footerLines?: string[];
	footerImageUrl?: string;
}

interface OrganizationLevelPDFContent {
	organizationId: string;
	organizationName: string;
	letterHeads: LetterHeadDetails[]; // Array of letterheads. Empty officeIds means applies to all offices
	topLeftText?: string[];
	footerImageUrl?: string;
	title: string;
	headerInfo: HeaderInfoItem[]; // key-value pairs for header info
	terms?: string; // Legal Terms of the PDF
	footerLines?: string[]; // Footer text
	officeFooters?: OfficeFooter[]; // Per-office overrides of footerLines/footerImageUrl; anything not overridden keeps the default
	verboseSowPdfMode?: VerboseSowPdfMode; // Controls per-line-item prices in the verbose Statement of Work PDF
}

interface PDFExportConfigModalProps {
	organizationId: string;
	organizationName: string;
	availableOffices: string[]; // Offices from organization settings
	onClose: () => void;
	onSave?: (config: OrganizationLevelPDFContent) => Promise<void>;
}

const PDFExportConfigModal: React.FC<PDFExportConfigModalProps> = ({
	organizationId,
	organizationName,
	availableOffices,
	onClose,
	onSave,
}) => {
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(true);
	const [config, setConfig] = useState<OrganizationLevelPDFContent>({
		organizationId,
		organizationName,
		letterHeads: [],
		title: "",
		headerInfo: [],
		topLeftText: [],
		footerLines: [],
		officeFooters: [],
	});

	// Fetch existing config and offices when modal opens
	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				// Fetch PDF config
				const response = await RestAPI.fetchAPI(`/rest/access/organization/${organizationId}/pdfExportConfig`, "GET");

				if (response) {
					// Migrate old string[] headerInfo to new HeaderInfoItem[] format if needed
					let headerInfo = response.headerInfo || [];
					if (headerInfo.length > 0 && typeof headerInfo[0] === "string") {
						// Convert old format to new format
						headerInfo = (headerInfo as string[]).map((item) => ({ label: item, field: "" as const }));
					}

					// Initialize letterHeads - migrate old letterHeadDetails if needed
					let letterHeads: LetterHeadDetails[] = response.letterHeads || [];

					// If we have old letterHeadDetails but no letterHeads, migrate it
					// Empty officeNames means "applies to all offices"
					if (letterHeads.length === 0 && response.letterHeadDetails) {
						letterHeads = [
							{
								...response.letterHeadDetails,
								officeNames: [], // Empty array means applies to all offices
							},
						];
					}

					const newConfig: OrganizationLevelPDFContent = {
						organizationId,
						organizationName,
						title: response.title || "",
						headerInfo,
						letterHeads,
						topLeftText: response.topLeftText || [],
						footerLines: response.footerLines || [],
						officeFooters: response.officeFooters || [],
						footerImageUrl: response.footerImageUrl,
						terms: response.terms,
						verboseSowPdfMode: response.verboseSowPdfMode,
					};
					setConfig(newConfig);
				}
			} catch (err) {
				logError("Error fetching PDF export config", err);
				// Keep default values on error
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [organizationId]);

	const updateConfig = (updates: Partial<OrganizationLevelPDFContent>) => {
		setConfig((prev) => ({ ...prev, ...updates }));
	};

	const addArrayItem = (field: "topLeftText" | "footerLines") => {
		setConfig((prev) => ({
			...prev,
			[field]: [...(prev[field] || []), ""],
		}));
	};

	const addHeaderInfoItem = () => {
		setConfig((prev) => ({
			...prev,
			headerInfo: [...(prev.headerInfo || []), { key: "", estimateField: EstimateFieldsForExportHeader.ProjectName }],
		}));
	};

	const updateArrayItem = (field: "topLeftText" | "footerLines", index: number, value: string) => {
		setConfig((prev) => {
			const newArray = [...(prev[field] || [])];
			newArray[index] = value;
			return { ...prev, [field]: newArray };
		});
	};

	const updateHeaderInfoItem = (index: number, item: HeaderInfoItem) => {
		setConfig((prev) => {
			const newArray = [...(prev.headerInfo || [])];
			newArray[index] = item;
			return { ...prev, headerInfo: newArray };
		});
	};

	const removeArrayItem = (field: "topLeftText" | "footerLines", index: number) => {
		setConfig((prev) => {
			const newArray = [...(prev[field] || [])];
			newArray.splice(index, 1);
			return { ...prev, [field]: newArray };
		});
	};

	const removeHeaderInfoItem = (index: number) => {
		setConfig((prev) => {
			const newArray = [...(prev.headerInfo || [])];
			newArray.splice(index, 1);
			return { ...prev, headerInfo: newArray };
		});
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			if (onSave) {
				await onSave(config);
			} else {
				// Default save implementation
				await RestAPI.fetchAPI(`/rest/access/organization/${organizationId}/pdfExportConfig`, "PUT", {
					pdfExportConfig: config,
				});
				toastNotify("PDF export config saved successfully", false);
			}
			onClose();
		} catch (err) {
			logError("Error saving PDF export config", err);
			toastNotify("Error saving PDF export config", true);
		} finally {
			setSaving(false);
		}
	};

	return (
		<TopMostPortal>
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
				<div className="bg-white w-[900px] max-h-[90vh] rounded-xl shadow-lg flex flex-col">
					<div className="flex w-full h-12 bg-gray-100 rounded-t-xl justify-between items-center px-4 border-b border-gray-200">
						<div className="text-gray-900 text-lg font-semibold font-['Inter'] leading-7 m-4">PDF Export Config</div>
						<button onClick={onClose} className="cursor-pointer p-1 hover:bg-gray-200 rounded">
							<img className="w-5 h-5" src="/assets/ui/x.svg" alt="Close" />
						</button>
					</div>
					<div className="flex-1 overflow-y-auto p-6 bg-gray-50">
						{loading ? (
							<div className="flex items-center justify-center h-64">
								<div className="text-gray-600">Loading PDF export config...</div>
							</div>
						) : (
							<div className="space-y-6">
								{/* Organization Info (Read-only) */}
								<div className="p-4 bg-white rounded-lg border border-gray-200">
									<div className="text-gray-900 text-base font-medium font-['Hanken Grotesk'] mb-2">
										Organization Information
									</div>
									<div className="space-y-2 text-sm">
										<div>
											<span className="text-gray-600">Organization ID:</span>{" "}
											<span className="text-gray-900">{config.organizationId}</span>
										</div>
										<div>
											<span className="text-gray-600">Organization Name:</span>{" "}
											<span className="text-gray-900">{config.organizationName}</span>
										</div>
									</div>
								</div>

								{/* Title */}
								<div className="p-4 bg-white rounded-lg border border-gray-200">
									<label className="block text-gray-900 text-base font-medium font-['Hanken Grotesk'] mb-2">
										Title <span className="text-red-500">*</span>
									</label>
									<div className="text-gray-500 text-sm mb-2">
										Title of the Exported PDF (i.e. "Proposal", "Preliminary Estimate, ..etc)
									</div>
									<input
										type="text"
										value={config.title}
										onChange={(e) => updateConfig({ title: e.target.value })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
										placeholder="Enter PDF title"
									/>
								</div>

								{/* Verbose Statement of Work PDF Mode */}
								<VerboseSowPdfModeField
									value={config.verboseSowPdfMode ?? VerboseSowPdfMode.WITH_LINE_ITEM_PRICES}
									onChange={(verboseSowPdfMode) => updateConfig({ verboseSowPdfMode })}
								/>

								{/* Letter Head Details */}
								<div className="p-4 bg-white rounded-lg border border-gray-200">
									<LetterHeadManagement
										letterHeads={config.letterHeads || []}
										availableOffices={availableOffices}
										organizationId={organizationId}
										onLetterHeadsChange={(letterHeads) => updateConfig({ letterHeads })}
									/>
								</div>

								{/* Top Left Text */}
								<ArrayInputField
									label="Top Left Text (optional)"
									items={config.topLeftText || []}
									placeholder={(index) => `Line ${index + 1}`}
									onAdd={() => addArrayItem("topLeftText")}
									onChange={(index, value) => updateArrayItem("topLeftText", index, value)}
									onRemove={(index) => removeArrayItem("topLeftText", index)}
									emptyMessage="No top left text lines added"
								/>

								{/* Footer Image */}
								<div className="p-4 bg-white rounded-lg border border-gray-200">
									<ImageUploadField
										label="Default Footer Image (optional)"
										imagePath={config.footerImageUrl}
										organizationId={organizationId}
										uploadDestination="footer"
										onImageChange={(path) => updateConfig({ footerImageUrl: path })}
										onImageRemove={() => updateConfig({ footerImageUrl: "" })}
										altText="Footer image preview"
									/>
								</div>

								{/* Header Info */}
								<HeaderInfoField
									items={config.headerInfo || []}
									onAdd={addHeaderInfoItem}
									onChange={updateHeaderInfoItem}
									onRemove={removeHeaderInfoItem}
									emptyMessage="No header info items added"
								/>

								{/* Terms */}
								<div className="p-4 bg-white rounded-lg border border-gray-200">
									<label className="block text-gray-900 text-base font-medium font-['Hanken Grotesk'] mb-2">
										Terms (optional)
									</label>
									<textarea
										value={config.terms || ""}
										onChange={(e) => updateConfig({ terms: e.target.value || undefined })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
										placeholder="Enter legal terms (e.g., estimate validity period)"
									/>
								</div>

								{/* Footer Lines */}
								<ArrayInputField
									label="Default Footer Lines (optional)"
									items={config.footerLines || []}
									placeholder={(index) => `Footer line ${index + 1}`}
									onAdd={() => addArrayItem("footerLines")}
									onChange={(index, value) => updateArrayItem("footerLines", index, value)}
									onRemove={(index) => removeArrayItem("footerLines", index)}
									emptyMessage="No footer lines added"
								/>

								{/* Office Footers */}
								<OfficeFooterManagement
									officeFooters={config.officeFooters || []}
									availableOffices={availableOffices}
									organizationId={organizationId}
									onOfficeFootersChange={(officeFooters) => updateConfig({ officeFooters })}
								/>
							</div>
						)}
					</div>
					<div className="w-full h-20 bg-white border-t border-gray-200 flex items-center justify-end gap-4 px-6 rounded-b-xl mt-4">
						<LassoSimpleButtonFull
							caption="Cancel"
							onClick={onClose}
							customButtonStyle={{ backgroundColor: "white", border: "1px solid #EAECF0" }}
							customTextStyle={{ color: "#344054" }}
							disabled={saving || loading}
						/>
						<LassoSimpleButtonFull
							caption={saving ? "Saving..." : "Save"}
							onClick={handleSave}
							disabled={saving || loading || !config.title}
						/>
					</div>
				</div>
			</div>
		</TopMostPortal>
	);
};

export default PDFExportConfigModal;
